/**
 * Pure function to calculate achievement based on total completed trainings
 * This logic is extracted from usePractitionerHighlights for testability
 */

export interface IAchievement {
  milestone: number;
  title: string;
  description: string;
  emojis: string[];
  numberText: string;
  currentCount: number;
}

/**
 * Helper function to get ordinal number
 */
const getOrdinalNumber = (num: number, t: (key: string) => string): string => {
  if (num === 1) return t('home.achievements.ordinal.first');
  if (num === 2) return t('home.achievements.ordinal.second');
  if (num === 3) return t('home.achievements.ordinal.third');
  // For 4-9, use the pattern
  return t('home.achievements.ordinal.number', { number: num });
};

/**
 * Calculate achievement based on total completed trainings
 * @param totalCompleted - Total number of completed trainings
 * @param t - Translation function from useI18n
 * @returns Achievement object or null if no achievement
 */
export const calculateAchievement = (totalCompleted: number, t: (key: string, params?: any) => string): IAchievement | null => {
  if (totalCompleted >= 1000) {
    return {
      milestone: 1000,
      title: t('home.achievements.milestones.1000.title'),
      description: t('home.achievements.milestones.1000.description'),
      emojis: ["⚔️", "📜"],
      numberText: t('home.achievements.milestones.1000.number_text'),
      currentCount: totalCompleted
    };
  } else if (totalCompleted >= 500) {
    return {
      milestone: 500,
      title: t('home.achievements.milestones.500.title'),
      description: t('home.achievements.milestones.500.description'),
      emojis: ["🏅", "🗺️"],
      numberText: t('home.achievements.milestones.500.number_text'),
      currentCount: totalCompleted
    };
  } else if (totalCompleted >= 200) {
    return {
      milestone: 200,
      title: t('home.achievements.milestones.200.title'),
      description: t('home.achievements.milestones.200.description'),
      emojis: ["⭐", "👑"],
      numberText: t('home.achievements.milestones.200.number_text'),
      currentCount: totalCompleted
    };
  } else if (totalCompleted >= 100) {
    return {
      milestone: 100,
      title: t('home.achievements.milestones.100.title'),
      description: t('home.achievements.milestones.100.description'),
      emojis: ["💯", "🔥"],
      numberText: t('home.achievements.milestones.100.number_text'),
      currentCount: totalCompleted
    };
  } else if (totalCompleted >= 50) {
    return {
      milestone: 50,
      title: t('home.achievements.milestones.50.title'),
      description: t('home.achievements.milestones.50.description'),
      emojis: ["🗓️", "✨"],
      numberText: t('home.achievements.milestones.50.number_text'),
      currentCount: totalCompleted
    };
  } else if (totalCompleted >= 25) {
    return {
      milestone: 25,
      title: t('home.achievements.milestones.25.title'),
      description: t('home.achievements.milestones.25.description'),
      emojis: ["⭐", "💪"],
      numberText: t('home.achievements.milestones.25.number_text'),
      currentCount: totalCompleted
    };
  } else if (totalCompleted >= 10) {
    return {
      milestone: 10,
      title: t('home.achievements.milestones.10.title'),
      description: t('home.achievements.milestones.10.description'),
      emojis: ["🔟", "💪"],
      numberText: t('home.achievements.milestones.10.number_text'),
      currentCount: totalCompleted
    };
  } else if (totalCompleted >= 1) {
    // Show current count when between milestones (1-9)
    return {
      milestone: totalCompleted, // Use current count as milestone for display
      title: totalCompleted === 1 
        ? t('home.achievements.milestones.1.title')
        : t('home.achievements.milestones.in_progress.title'),
      description: totalCompleted === 1 
        ? t('home.achievements.milestones.1.description')
        : t('home.achievements.milestones.in_progress.description'),
      emojis: totalCompleted === 1 ? ["👣", "👣", "🥋"] : ["💪", "🥋"],
      numberText: getOrdinalNumber(totalCompleted, t),
      currentCount: totalCompleted
    };
  }

  return null;
};

